import { Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { Observable, forkJoin } from 'rxjs'
import { map, startWith } from 'rxjs/operators'
import { CallerService } from '../services/caller.service'
import { HttpResponse, HttpStatusCode } from '@angular/common/http'
import { profileUpdateSuccess } from '../ngrx-store/actions/corp-member.actions'
import { ActivatedRoute } from '@angular/router'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import * as Sentry from "@sentry/angular";
import { Clipboard } from '@angular/cdk/clipboard'

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit {
    constructor(
        private activatedRoute: ActivatedRoute,
        public callerService: CallerService,
        private clipboard: Clipboard,
    ) {}
    @ViewChild('profilePhotoInput', { read: ElementRef<HTMLInputElement> })
        profilePhotoInput!: ElementRef<HTMLInputElement>
    

    INVITE_TEXT = `Join me on corpers.ng! Sign up with my referral code ${window.location.origin}/signup?ref=${this.callerService.corpMember.slug_id}`
    filteredTypeOfInstitutionOptions: Observable<string[]> | undefined

    stateCodeInputDisabled = false

    isUpdatingProfile = false

    ng_states: any[] = []
    ng_states_and_lgas: any[] = []
    selected_state_lgas: any[] = []

    PPAsInSelectedLGA: Array<any> = []
    filteredLgaPPAs: Observable<any[]> | undefined;

    serviceDetailsFormGroup: FormGroup = new FormGroup({
        service_state: new FormControl(
            this.callerService.corpMember?.service_state,
            [Validators.required],
        ),
        lga_id: new FormControl(this.callerService.corpMember?.lga_id, [
            Validators.required,
        ]),
        city_or_town: new FormControl(
            this.callerService.corpMember?.city_or_town,
        ),
        stream: new FormControl(this.callerService.corpMember?.stream),
        state_code: new FormControl(this.callerService.corpMember?.state_code, [
            Validators.required,
            Validators.pattern(this.callerService.state_code_regex),
        ]),
    })

    personalDetailsFormGroup: FormGroup = new FormGroup({
        first_name: new FormControl(this.callerService.corpMember?.first_name),
        last_name: new FormControl(this.callerService.corpMember?.last_name),
        nickname: new FormControl(this.callerService.corpMember?.nickname),
    })

    otherDetailsFormGroup: FormGroup = new FormGroup({
        travel_from_state: new FormControl(
            this.callerService.corpMember.travel_from_state,
        ),
        where_they_found_accommodation: new FormControl(null),
        travel_from_city: new FormControl(
            this.callerService.corpMember.travel_from_city,
        ),
        want_spa_or_not: new FormControl(
            this.callerService.corpMember.want_spa_or_not,
        ),
        looking_for_accommodation_or_not: new FormControl(
            this.callerService.corpMember.looking_for_accommodation_or_not,
        ),
    })

    ppaDetailsFormGroup: FormGroup = new FormGroup({
        ppa_name: new FormControl(this.callerService.corpMember.PPA?.name),
        type_of_institution: new FormControl(this.callerService.corpMember.PPA?.type_of_ppa),
        
        // we'll pick the one you selected/added - the api returns just once associated location (by the user)
        ppa_id: new FormControl(this.callerService.corpMember.PPA?.id),
        ppa_address: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.address),
        ppa_directions: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.directions),
        location_id: new FormControl(this.callerService.corpMember.PPA?.Locations?.[0]?.id),
    }, { validators: [ this.requireBothFieldsValidator('ppa_name', 'type_of_institution') ] })

    bioDetailsFormGroup: FormGroup = new FormGroup({
        public_profile: new FormControl(
            this.callerService.corpMember?.public_profile,
        ),
        nickname: new FormControl(this.callerService.corpMember?.nickname),
        bio: new FormControl(this.callerService.corpMember?.bio),
    })

    // combo form
    profileFormGroup: FormGroup = new FormGroup({
        bioDetails: this.bioDetailsFormGroup,
        ppaDetails: this.ppaDetailsFormGroup,
        otherDetails: this.otherDetailsFormGroup,
        serviceDetails: this.serviceDetailsFormGroup,
        personalDetails: this.personalDetailsFormGroup,
    })

    serviceStreams: Array<string> = ['1', '2', '3']

    /**
     * if control1 is filled, and control2 is not, then control2 is required
     * if control2 is filled, and control1 is not, then control1 is required
     * if control1 and control2 is not filled, clear errors
     * @param field1 
     * @param field2 
     * @returns 
     * 
     * from chat gpt
     */
    requireBothFieldsValidator(field1: string, field2: string): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
          
            const control1 = group.get(field1);
            const control2 = group.get(field2);
        
            if (!control1 || !control2) return null; // Safety check
        
            const value1 = control1.value;
            const value2 = control2.value;

            if (!value1 && !value2) {
                if (control1?.errors?.['required']) {
                    delete control1?.errors?.['required'];
                    control1.updateValueAndValidity();
                }

                if (control2?.errors?.['required']) {
                    delete control2?.errors?.['required'];
                    control2.updateValueAndValidity();
                }
                
                return null
            }
        
            if (value1 && !value2) {
                // If either is filled and the other is not, error
                control2.setErrors({ required: true });
                return { required: true };
            }

            if (value2 && !value1) {
                // If either is filled and the other is not, error
                control1.setErrors({ required: true });
                return { required: true };
            }

            if (control1?.value && control1?.errors?.['required']) {
                delete control1?.errors?.['required'];
                control1.updateValueAndValidity();
            }

            if (control2?.value && control2?.errors?.['required']) {
                delete control2?.errors?.['required'];
                control2.updateValueAndValidity();
            }
        
            return null; // Valid
        };
      }

    ngOnInit(): void {
        this.activatedRoute.data.subscribe(({ res }) => {
            // do something with your resolved data ...
            console.log('2nd resolved data', res)

            if (res.status === HttpStatusCode.Ok) {
                this.ng_states_and_lgas = res.body?.states_and_lgas

                // This should be string options.
                this.ng_states = this.ng_states_and_lgas.map((s, i) => ({
                    index: i,
                    id: s.id,
                    name: s.name,
                }))
            }
        })

        this.serviceDetailsFormGroup.valueChanges.subscribe({
            next: (values) => {
                // console.log('service details values', values);
            },
        })

        this.serviceDetailsFormGroup
            .get(['service_state'])
            ?.valueChanges.subscribe({
                next: (values) => {
                    console.log('new service state', values)
                    // clear lga value on service state change.
                    this.serviceDetailsFormGroup.get(['lga'])?.setValue('')
                },
            })

        this.bioDetailsFormGroup.valueChanges.subscribe({
            next: (values) => {
                // console.log('values', values);
            },
        })

        this.ppaDetailsFormGroup?.get('ppa_name')?.valueChanges.subscribe({
            next: (values) => {
                console.log('new ppa name', values);
            },
        })

        // @todo: only run this when the lga changes.
        this.callerService.fetchPPAsInLGA(this.callerService.corpMember.lga_id).subscribe({
          next: (res: HttpResponse<any>) => {
              console.log('fetchPPAsInLGA res', res)
              if (res.status === 200) {
                  // todo: show options (how can we optimize this for when the options are a lot??)

                  this.PPAsInSelectedLGA = res.body.ppas

                  this.filteredLgaPPAs = this.ppaDetailsFormGroup?.get('ppa_name')?.valueChanges.pipe(
                      startWith(''),
                      map(value => this._filterLGAs(value || '')),
                  );
              } else {
                  // else show error message?
              }
          },
          error: (err) => {
            Sentry.captureException(err);
              console.log('fetchPPAsInLGA err', err)
          },
      })

      this.filteredTypeOfInstitutionOptions = this.ppaDetailsFormGroup.get(['type_of_institution'])
        ?.valueChanges.pipe(
            startWith(''),
            map(value => this._filterTypeOfInstitutionOptions(value || '')),
        );

        console.log('hey, this is me', this.callerService.corpMember)
    }

    copyInviteText() {
        const copied = this.clipboard.copy(this.INVITE_TEXT)
        if (copied) {
            this.callerService.showNotification('Invite text copied to clipboard. Share with your friends!')
        }
    }

    // @duplicated method
    // update the type of institution (and address?)
    ppaSelected(event: MatAutocompleteSelectedEvent) {
        console.log('ppa selected', event)

        const ppa = this.PPAsInSelectedLGA.find((ppaOption) => ppaOption.id === event.option.id)
        console.log('selected ppa', ppa);
        if (ppa) {
            this.ppaDetailsFormGroup.get('type_of_institution')?.setValue(ppa?.type_of_ppa)
            // @todo: also update the address??
        }
        
    }

    displayPpaName(value: any) {
        if (typeof value === 'object') {
            return value?.name
        }
        // else it's string
        return value
    }

    private _filterTypeOfInstitutionOptions(value: string): string[] {
        return CallerService.PPA_TYPE_OPTIONS
        .filter((option: string) => option?.toLowerCase().includes(value.toLowerCase()));
    }

    private _filterLGAs(value: string): string[] {
      const filterValue = this.displayPpaName(value).toLowerCase();

      return this.PPAsInSelectedLGA?.filter((option: any) => option?.name?.toLowerCase().includes(filterValue));
  }

    setSelectedStatesLGA(event: any, value: any) {
        this.selected_state_lgas =
            this.ng_states_and_lgas?.[value.index]?.StateLGAs
    }

    toggleStateCodeInputDisabled() {
        this.stateCodeInputDisabled = !this.stateCodeInputDisabled
    }

    /**
     * Depreciated
     *
     * Missing personalDetailsFormGroup values
     */
    updateCorpMemberProfile(): void {
        console.log('clicked Update!')

        // check this.serviceDetailsFormGroup.pristine
        if (this.serviceDetailsFormGroup.pristine) {
            this.callerService
                .updateProfile(this.serviceDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated updateProfile', res)
                    },
                    error: (err) => {
                        Sentry.captureException(err);
                        this.callerService.showNotification(
                            'Failed to update Profile Details',
                        )
                    },
                })
        }

        if (this.bioDetailsFormGroup.dirty) {
            this.callerService
                .updateProfileBio(this.bioDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated updateProfileBio', res)
                    },
                    error: (err) => {
                        this.callerService.showNotification(
                            'Failed to update Bio Details',
                        )
                    },
                })
        }

        if (this.ppaDetailsFormGroup.dirty) {
            this.callerService
                .updateProfilePpaDetails(this.ppaDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated updateProfilePpaDetails', res)
                    },
                    error: (err) => {
                        this.callerService.showNotification(
                            'Failed to update PPA Details',
                        )
                    },
                })
        }

        if (this.otherDetailsFormGroup.dirty) {
            this.callerService
                .updateProfileOtherDetails(this.otherDetailsFormGroup.value)
                .subscribe({
                    next: (res) => {
                        console.log('updated otherDetailsFormGroup', res)
                    },
                    error: (err) => {
                        this.callerService.showNotification(
                            'Failed to update Other Details',
                        )
                    },
                })
        }
    }

    /**
     * Upgrade method from updateCorpMemberProfile().
     *
     * update with ~Promise.all~ forkJoin()
     * Create ngrx actions to cater for this.
     *
     * Missing personalDetailsFormGroup values
     */
    updateEverything(): void {
        // trying formgroup of formgroups
        console.log('profileFormGroup', this.profileFormGroup.value)

        const updateMethods = []

        if (this.otherDetailsFormGroup.dirty) {
            updateMethods.push(
                this.callerService.updateProfileOtherDetails(
                    this.otherDetailsFormGroup.value,
                ),
            )
        }

        if (this.ppaDetailsFormGroup.dirty) {
            updateMethods.push(
                this.callerService.updateProfilePpaDetails(
                    this.ppaDetailsFormGroup.value,
                ),
            )
        }

        if (this.bioDetailsFormGroup.dirty) {
            updateMethods.push(
                this.callerService.updateProfileBio(
                    this.bioDetailsFormGroup.value,
                ),
            )
        }

        if (this.serviceDetailsFormGroup.pristine) {
            updateMethods.push(
                this.callerService.updateProfileServiceDetails(
                    this.serviceDetailsFormGroup.value,
                ),
            )
        }

        forkJoin(updateMethods).subscribe((values) => {
            console.log('updateEverything res??', values)

            if (values.every((res) => res.status === HttpStatusCode.Ok)) {
                this.callerService.showNotification('Profile updated')
            } else {
                // TODO: Find the sections that didn't update, and show an appropriate message.
                this.callerService.showNotification('Profile update failed.')
            }
        })
    }

    /**
     * Upgrade from updateEverything().
     */
    updateProfile(): void {
        this.updateProfilePicture()
        
        if (!this.profileFormGroup.valid) {
            // console.log('errrs', this.profileFormGroup.errors)

            // console.log({
            //     bioDetails: this.bioDetailsFormGroup.errors,
            //     ppaDetails: this.ppaDetailsFormGroup,
            //     otherDetails: this.otherDetailsFormGroup,
            //     serviceDetails: this.serviceDetailsFormGroup,
            //     personalDetails: this.personalDetailsFormGroup,
            // });

            this.ppaDetailsFormGroup.markAsTouched()
            this.ppaDetailsFormGroup.updateValueAndValidity()
            
            this.callerService.showNotification('Fix errors in the form.')
            return
        }

        if (!this.profileFormGroup.dirty) {
            console.log('profileFormGroup dirty', this.profileFormGroup.dirty)
            return
            
        }
        
        this.isUpdatingProfile = true
        this.callerService
            .updateProfile({
                ...this.serviceDetailsFormGroup.value,
                ...this.bioDetailsFormGroup.value,
                ...this.ppaDetailsFormGroup.value,
                ...this.otherDetailsFormGroup.value,
                ...this.personalDetailsFormGroup.value,
            })
            .subscribe({
                next: (res: HttpResponse<any>) => {
                    console.log('updated profile', res)
                    if (res.status === HttpStatusCode.Ok) {
                        this.callerService.store.dispatch(
                            // @todo: fix this. ?? 
                            profileUpdateSuccess({ data: res.body.result }),
                        )

                        this.callerService.showNotification('Profile updated')
                    }
                    this.isUpdatingProfile = false
                },
                error: (err) => {
                    console.log('NOT updated profile', err)
                    this.callerService.showNotification(
                        err?.error?.message ?? 'Failed to update profile',
                    )
                    this.isUpdatingProfile = false

                    Sentry.captureException(err);
                },
                complete: () => {},
            })
    }

    // no abrupt return here.
    updateProfilePicture(): void {
        console.log('this.pp.has(pic)', this.pp.has('pic'))
        if (this.pp.has('pic')) {
            this.isUpdatingProfile = true
            this.callerService.updateProfilePicture(this.pp)
                .subscribe({
                    next: (res) => {
                    
                        if (res.body) {
                            const _res = JSON.parse(res.body)
                            console.log('pic res', _res)
                            const profilePicUrl = _res.urls?.[0]

                            this.callerService.store.dispatch(
                                profileUpdateSuccess({ data: { Media : _res.Media } }),
                            )
                        }

                        this.pp = new FormData() // reset
                        this.profilePhotoInput.nativeElement.value = '' // reset the input value

                        this.isUpdatingProfile = false

                        this.callerService.showNotification('Profile picture updated')
                    },
                    error: (err) => {
                        console.log('pic ??? ERR', err)

                        this.isUpdatingProfile = false

                        this.callerService.showNotification('Failed to update profile picture')
                        Sentry.captureException(err);
                    }
                })
        }
        
    }

    pp: FormData = new FormData()
    selectedImage: string = ''

    onFileSelected(event: Event): void {
        console.log('evt', event)

        const target = event.target as HTMLInputElement
        const file = target.files?.[0]

        console.log('UI', file)

        if (file) {
            const reader = new FileReader() // do we wanna define this every time the function is called?

            reader.onerror = function () {
                console.log('FileReader Err:', reader.error)
            }

            reader.onload = (e: any) => {
                console.log('e', e)
                this.selectedImage = e.target.result
            }

            reader.readAsDataURL(file)

            // `media-${file.lastModified}-${file.size}-${file.name}`
            this.pp.set('pic', file) // setting individually so it's easier to delete.

        }
    }



    get corpMemberSelectedLgaName(): string {
        return this.selected_state_lgas.find(
            (opt) =>
                opt.id === this.serviceDetailsFormGroup.get(['lga_id'])?.value,
        )?.name
    }

    /**
     * The idea is that we'll show suggestions of ppa in the LGA they select.
     */
    getAllPpaInSelectedLga() {}
}
